<script setup lang="ts">
import { computed, useAttrs } from 'vue';
import type { TextareaProps } from '../types';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import FormInputDisplay from '../display/FormInputDisplay.vue';
import { getPrefixedUuid } from '../utils';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<TextareaProps>(), {
  mode: 'edit',
  id: () => getPrefixedUuid('textfield'),
});

const passdownProps = computed(() => ({
  ...props,
  label: undefined,
  useMarkdown: undefined,
}));

const attrs = useAttrs();
const passdownAttrs = computed(() => ({
  ...attrs,
  class: undefined,
}));

const emits = defineEmits(['update:modelValue']);

function onUpdate(input: string) {
  emits('update:modelValue', input.trim() || props.emptyValue);
}
</script>

<template>
  <div :class="$attrs.class">
    <FormLabel :for="id" :infoMessage="infoMessage" :label="label" :disabled="$props.disabled">
      <template #default="{ onBlur, onFocus, hoverProps }">
        <FormInputDisplay
          v-if="mode === 'details' || mode === 'execution'"
          :value="modelValue"
          :useMarkdown="props.useMarkdown"
          :maxRows="props.maxRows"
          :placeholder="props.placeholder"
        />
        <v-textarea
          v-else
          v-bind="{ ...passdownProps, ...passdownAttrs, ...hoverProps }"
          :modelValue="modelValue"
          :error="Boolean(errorMessage)"
          :hint="errorMessage || hint"
          :persistentHint="Boolean(errorMessage) || persistentHint"
          @update:modelValue="onUpdate"
          @focus="onFocus()"
          @blur="onBlur()"
        />
      </template>
    </FormLabel>
  </div>
</template>
